import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  link?: string;
  createdAt: string;
  read: boolean;
  // Optional metadata for enhanced notifications
  metadata?: {
    ticketId?: string;
    ticketNumber?: string;
    severity?: 'minor' | 'major' | 'critical';
    breachType?: string;
    recipientRole?: 'assignee' | 'admin' | 'team_lead';
    assigneeName?: string;
  };
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isOpen: boolean;
  dismissedIds: string[]; // IDs that the user deleted — never re-add from API

  // Actions
  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  setIsOpen: (isOpen: boolean) => void;
  toggleOpen: () => void;
  isDismissed: (id: string) => boolean;
}

const MAX_NOTIFICATIONS = 50;

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      isOpen: false,
      dismissedIds: [],

      addNotification: (notification) => {
        set((state) => {
          const notifications = [notification, ...state.notifications].slice(
            0,
            MAX_NOTIFICATIONS
          );
          const unreadCount = notifications.filter((n) => !n.read).length;
          return { notifications, unreadCount };
        });
      },

      markAsRead: (id) => {
        set((state) => {
          const notifications = state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          );
          const unreadCount = notifications.filter((n) => !n.read).length;
          return { notifications, unreadCount };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      removeNotification: (id) => {
        set((state) => {
          const notifications = state.notifications.filter((n) => n.id !== id);
          const unreadCount = notifications.filter((n) => !n.read).length;
          const dismissedIds = [...state.dismissedIds, id].slice(-200); // keep last 200
          return { notifications, unreadCount, dismissedIds };
        });
      },

      clearAll: () => {
        set((state) => {
          const dismissedIds = [
            ...state.dismissedIds,
            ...state.notifications.map((n) => n.id),
          ].slice(-200);
          return { notifications: [], unreadCount: 0, dismissedIds };
        });
      },

      isDismissed: (id) => {
        return get().dismissedIds.includes(id);
      },

      setIsOpen: (isOpen) => {
        set({ isOpen });
      },

      toggleOpen: () => {
        set((state) => ({ isOpen: !state.isOpen }));
      },
    }),
    {
      name: 'centraoneplus-notifications',
      partialize: (state) => ({
        notifications: state.notifications.slice(0, 20), // Only persist last 20
        dismissedIds: state.dismissedIds.slice(-200),
      }),
    }
  )
);
